/**
 * AJ Events — public frontend runtime.
 *
 * Two jobs:
 *  1. Occurrence switching — clicking a date in the selector swaps the
 *     "this date" content client-side (no navigation) and updates the URL to
 *     the occurrence deep-link (history.pushState) + the canonical link.
 *  2. The slide-out RSVP panel — open/close, ticket-count → attendee inputs,
 *     and submission to the aj-events/v1/register endpoint.
 *
 * Config is injected via wp_localize_script as window.AJ_EVENTS.
 */

(() => {
  const cfg = window.AJ_EVENTS || {};
  const root = document.querySelector('.aj-ev-single');
  const dataEl = document.getElementById('aj-ev-data');

  // Subscribe forms and the post-event feedback form are independent of the
  // single-event occurrence runtime (the feedback form shows on passed events
  // too, where the occurrence data element may be absent), so bind them before
  // the runtime guard below — otherwise the star rating wouldn't be clickable.
  bindSubscribeForms();
  bindFeedbackForm();

  if (!dataEl) return;

  let model;
  try {
    model = JSON.parse(dataEl.textContent);
  } catch (e) {
    return;
  }

  const bySlug = {};
  (model.occurrences || []).forEach((o) => { bySlug[o.slug] = o; });
  let current = bySlug[model.selectedSlug] || (model.occurrences || [])[0];

  // Post-checkout flash (Stripe success/cancel redirect).
  (() => {
    const p = new URLSearchParams(window.location.search);
    let msg = '';
    if (p.get('aj_ev_paid')) msg = 'Payment received — you’re registered! Check your email for confirmation.';
    else if (p.get('aj_ev_canceled')) msg = 'Checkout canceled — you weren’t charged.';
    if (msg && root) {
      const b = document.createElement('div');
      b.className = 'aj-ev-flash';
      b.textContent = msg;
      root.prepend(b);
    }
  })();

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const setText = (key, value) => {
    document.querySelectorAll(`[data-aj="${key}"]`).forEach((el) => { el.textContent = value || ''; });
  };
  // Every match, not just the first: some blocks (the venue credit) are shown
  // in more than one place and all copies have to track the selected date.
  const setHtml = (key, value) => {
    document.querySelectorAll(`[data-aj="${key}"]`).forEach((el) => {
      el.innerHTML = value || '';
    });
  };

  /**
   * Build the CTA markup for an occurrence.
   *
   * @param {object} occ Occurrence view.
   * @returns {string} HTML.
   */
  function ctaHtml(occ) {
    if (!occ) return '';
    if (occ.closed) {
      return `<button class="aj-ev-btn aj-ev-btn--disabled" disabled>${occ.cta_label}</button>`;
    }
    if (occ.external_url) {
      // Humanitix links open in the popup widget: href must end in
      // /tickets?widget=popup, and we drop target=_blank so the widget (which
      // re-binds via MutationObserver) can intercept the click. The href is
      // pre-built server-side (popup-normalised + UTM-tagged) as external_href;
      // fall back to building it client-side for any older cached payload.
      const href = occ.external_href
        || (isHumanitix(occ.external_url) ? humanitixPopupUrl(occ.external_url) : occ.external_url);
      if (isHumanitix(occ.external_url)) {
        return `<a class="aj-ev-btn aj-ev-btn--primary" href="${href}" data-aj="rsvp-ext" data-occurrence="${occ.id}">${occ.cta_label}</a>`;
      }
      return `<a class="aj-ev-btn aj-ev-btn--primary" href="${href}" target="_blank" rel="noopener" data-aj="rsvp-ext" data-occurrence="${occ.id}">${occ.cta_label}</a>`;
    }
    if (occ.sold_out) {
      // Full → offer the waitlist (when the event allows it), else a dead button.
      if (occ.waitlist) {
        return `<button class="aj-ev-btn aj-ev-btn--primary aj-ev-btn--waitlist" data-aj="rsvp-open" data-occurrence="${occ.id}" data-waitlist="1">Join the waitlist</button>`;
      }
      return `<button class="aj-ev-btn aj-ev-btn--disabled" disabled>${occ.cta_label}</button>`;
    }
    return `<button class="aj-ev-btn aj-ev-btn--primary" data-aj="rsvp-open" data-occurrence="${occ.id}">${occ.cta_label}</button>`;
  }

  /**
   * Whether a URL points at Humanitix.
   *
   * @param {string} url URL.
   * @returns {boolean} True if Humanitix.
   */
  function isHumanitix(url) {
    try {
      return new URL(url, window.location.href).hostname.toLowerCase().includes('humanitix.com');
    } catch (e) {
      return false;
    }
  }

  /**
   * Normalise a Humanitix URL for the popup widget: path ends with /tickets and
   * the query contains widget=popup. Idempotent.
   *
   * @param {string} url Humanitix URL.
   * @returns {string} Popup URL.
   */
  function humanitixPopupUrl(url) {
    try {
      const u = new URL(url, window.location.href);
      const path = u.pathname.replace(/\/+$/, '');
      u.pathname = path.endsWith('/tickets') ? path : `${path}/tickets`;
      u.searchParams.set('widget', 'popup');
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  /**
   * Render the selected occurrence into the page.
   *
   * @param {object} occ Occurrence view.
   * @param {boolean} push Whether to push the deep-link into history.
   * @returns {void}
   */
  function select(occ, push) {
    if (!occ) return;
    current = occ;

    setText('occ-title', occ.title);
    setText('occ-weekday', occ.badge_weekday);
    setText('occ-day', occ.badge_day);
    setText('occ-month', occ.badge_month);
    setText('occ-when', occ.when_label);
    setHtml('occ-where', occ.where_label);
    setText('occ-price', occ.price_label);
    setText('occ-price2', occ.price_label);
    setHtml('occ-venue-logo', occ.venue_logo);
    setHtml('occ-map', occ.map_html);
    setHtml('occ-desc', occ.desc_html);
    setHtml('occ-people', occ.people_html);
    setHtml('occ-partners', occ.partners_html);

    document.querySelectorAll('[data-aj="cta"]').forEach((el) => { el.innerHTML = ctaHtml(occ); });
    if (root) root.classList.toggle('is-sold-out', !!occ.sold_out);
    // Fill the sold-out labels only when this date is actually full. They ship
    // empty from PHP: text hidden with CSS is still read by scrapers and AI
    // crawlers, which made every event look sold out.
    const soldOutLabel = occ.sold_out ? (cfg.i18n && cfg.i18n.soldOut) || 'Sold out' : '';
    setText('soldout-ribbon', soldOutLabel);
    setText('soldout-chip', soldOutLabel);
    renderCountdown();

    root.querySelectorAll('.aj-ev-selector__item').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.ajSlug === occ.slug);
    });
    scrollActiveIntoView(true);

    if (push && occ.deep_link) {
      window.history.pushState({ slug: occ.slug }, '', occ.deep_link);
      let canonical = $('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = occ.deep_link;
    }
  }

  // Live "registrations close in DD:HH:MM:SS" countdown under the CTA, ticking
  // every second. (Availability of seats is never shown — this is purely the
  // registrations-close deadline.)
  function formatCountdown(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const pad = (n) => String(n).padStart(2, '0');
    const days = Math.floor(total / 86400);
    const hrs = Math.floor((total % 86400) / 3600);
    const mins = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return `${pad(days)}:${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
  }
  function renderCountdown() {
    const el = $('[data-aj="occ-countdown"]');
    if (!el) return;
    const iso = current && current.close_iso;
    const ms = iso ? new Date(iso).getTime() - Date.now() : 0;
    if (!iso || ms <= 0) { el.hidden = true; el.textContent = ''; return; }
    el.hidden = false;
    el.textContent = `Registrations close in ${formatCountdown(ms)}`;
  }
  renderCountdown();
  setInterval(renderCountdown, 1000);

  // Date selector clicks.
  if (root) {
    root.addEventListener('click', (e) => {
      const item = e.target.closest('.aj-ev-selector__item');
      if (item) {
        e.preventDefault();
        select(bySlug[item.dataset.ajSlug], true);
      }
    });
  }

  window.addEventListener('popstate', (e) => {
    const slug = (e.state && e.state.slug) || model.selectedSlug;
    if (bySlug[slug]) select(bySlug[slug], false);
  });

  /* --------------------------------------------------------------------- */
  /* Occurrence carousel — nav buttons, edge fades, active centering       */
  /* --------------------------------------------------------------------- */

  const ctx = root || document;
  const selWrap = $('[data-aj="selectorwrap"]', ctx);
  const track = $('[data-aj="selector"]', ctx);
  const prevBtn = $('[data-aj="sel-prev"]', ctx);
  const nextBtn = $('[data-aj="sel-next"]', ctx);

  function updateNav() {
    if (!selWrap || !track) return;
    const max = track.scrollWidth - track.clientWidth;
    const scrollable = max > 4;
    selWrap.classList.toggle('is-scrollable', scrollable);
    selWrap.classList.toggle('at-start', track.scrollLeft <= 1);
    selWrap.classList.toggle('at-end', track.scrollLeft >= max - 1);
    if (prevBtn) prevBtn.disabled = track.scrollLeft <= 1;
    if (nextBtn) nextBtn.disabled = track.scrollLeft >= max - 1;
  }

  function scrollActiveIntoView(smooth) {
    if (!track) return;
    const active = track.querySelector('.aj-ev-selector__item.is-active');
    if (active) {
      active.scrollIntoView({ inline: 'center', block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
    }
  }

  if (track) {
    const page = () => Math.max(140, track.clientWidth * 0.8);
    if (prevBtn) prevBtn.addEventListener('click', () => track.scrollBy({ left: -page(), behavior: 'smooth' }));
    if (nextBtn) nextBtn.addEventListener('click', () => track.scrollBy({ left: page(), behavior: 'smooth' }));
    track.addEventListener('scroll', () => window.requestAnimationFrame(updateNav), { passive: true });
    window.addEventListener('resize', updateNav);
    updateNav();
    scrollActiveIntoView(false);
  }

  /* --------------------------------------------------------------------- */
  /* RSVP panel                                                            */
  /* --------------------------------------------------------------------- */

  const panel = $('[data-aj="rsvp-panel"]');
  const form = $('[data-aj="rsvp-form"]');
  const submitBtnEl = form ? form.querySelector('[type="submit"]') : null;
  const submitDefaultLabel = submitBtnEl ? (submitBtnEl.textContent || submitBtnEl.value || 'Reserve') : 'Reserve';
  let waitlistMode = false;

  /** Set a submit button's label (works for <button> and <input>). */
  function setBtnLabel(btn, text) {
    if (!btn) return;
    if (btn.value !== undefined && btn.tagName === 'INPUT') btn.value = text; else btn.textContent = text;
  }

  /**
   * Open the RSVP panel for the current occurrence.
   *
   * @param {number} occId Occurrence id.
   * @param {boolean} waitlist Open in "join the waitlist" mode.
   * @returns {void}
   */
  function openPanel(occId, waitlist) {
    // Register for the occurrence whose button was clicked (falls back to the
    // currently-selected one), so the saved date can never drift from the CTA.
    const occ = (occId && (model.occurrences || []).find((o) => o.id === occId)) || current;
    if (!panel || !occ) return;
    current = occ;
    waitlistMode = !!waitlist;
    $('[data-aj="rsvp-occ"]').value = occ.id;
    setText('rsvp-title', occ.title);
    setText('rsvp-when', occ.when_label);
    // Named dates ("Coffee #124 — West Village") don't say which event they
    // belong to, so name the series underneath. Hidden when the date has no name
    // of its own and the title is already the series name.
    const series = $('[data-aj="rsvp-series"]');
    if (series) {
      series.textContent = occ.series_title || '';
      series.hidden = !occ.series_title;
    }

    // Filled here rather than in select(), which only runs when someone switches
    // date — on first load it hasn't run, so the panel would open blank.
    setHtml('rsvp-where', occ.where_label);
    setHtml('rsvp-people', occ.people_html);
    setHtml('rsvp-partners', occ.partners_html);
    setBtnLabel(submitBtnEl, waitlistMode ? 'Join the waitlist' : submitDefaultLabel);
    const note = $('[data-aj="rsvp-waitlist-note"]');
    if (note) note.hidden = !waitlistMode;
    panel.hidden = false;
    lockScroll();
    syncViewport();
    resetQty();
    // Nothing is focused when the panel opens — deliberately. Focusing the first
    // field pops the keyboard and zooms the viewport on touch devices, which
    // reads as the form having grabbed the page. The panel is short enough that
    // there is nothing to hunt for, so people tap the field they want.
  }

  /**
   * Close the RSVP panel.
   *
   * @returns {void}
   */
  function closePanel() {
    if (!panel) return;
    panel.hidden = true;
    unlockScroll();
  }

  /* --- Scroll lock + on-screen-keyboard handling ------------------------- *
   * iOS ignores `overflow: hidden` on <body>, so the page behind the flyout
   * still scrolled. Pinning the body with `position: fixed` at its current
   * offset is the only reliable lock; the offset is restored on close so the
   * page doesn't jump back to the top.
   *
   * Separately, the keyboard shrinks the *visual* viewport but not the layout
   * viewport, so a full-height panel keeps running underneath it — that was the
   * gap. visualViewport reports the actually-visible strip, and the panel is
   * sized and positioned to match it. */

  let scrollLockY = 0;

  function lockScroll() {
    scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${scrollLockY}px`;
    document.body.classList.add('aj-ev-panel-open');
    // Shared signal across the aj-* plugins: "something modal is open, get out
    // of the way." Floating widgets (the toaster, the tip tab) hide on this
    // rather than each plugin having to know the others' class names.
    document.body.classList.add('aj-modal-open');
  }

  function unlockScroll() {
    document.body.classList.remove('aj-ev-panel-open');
    document.body.classList.remove('aj-modal-open');
    document.body.style.top = '';
    if (panel) {
      panel.style.removeProperty('--aj-ev-vvh');
      panel.style.removeProperty('--aj-ev-vvtop');
    }
    // Instant, so closing never animates the page back down.
    window.scrollTo(0, scrollLockY);
  }

  function syncViewport() {
    const vv = window.visualViewport;
    if (!vv || !panel || panel.hidden) return;
    panel.style.setProperty('--aj-ev-vvh', `${vv.height}px`);
    // While the keyboard is up iOS also shifts the visual viewport down.
    panel.style.setProperty('--aj-ev-vvtop', `${vv.offsetTop}px`);
  }

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', syncViewport);
    window.visualViewport.addEventListener('scroll', syncViewport);
  }

  /**
   * Replace the registration form with an animated success state.
   *
   * @returns {void}
   */
  function showRegistered(isWaitlist) {
    const sheet = panel && panel.querySelector('.aj-ev-panel__sheet');
    if (!sheet) return;

    const social = (cfg.social || []).map((s) => (
      `<a class="aj-ev-rsvp-done__link" href="${s.url}" target="_blank" rel="noopener">${s.label}</a>`
    )).join('');

    const title = isWaitlist ? 'You’re on the waitlist' : 'You’re registered!';
    const note = isWaitlist
      ? 'This event is full, so we’ve added you to the waitlist. We’ll email you right away if a spot opens up.'
      : 'Check your email for your confirmation, all the event details, and any updates.';

    sheet.innerHTML = `
      <button class="aj-ev-panel__x" data-aj="rsvp-close" aria-label="Close">&times;</button>
      <div class="aj-ev-rsvp-done">
        <span class="aj-ev-check" aria-hidden="true">
          <svg viewBox="0 0 52 52"><circle class="aj-ev-check__circle" cx="26" cy="26" r="23" fill="none"/><path class="aj-ev-check__mark" fill="none" d="M14 27 l8 8 l16 -17"/></svg>
        </span>
        <h3 class="aj-ev-rsvp-done__title">${title}</h3>
        <p class="aj-ev-rsvp-done__msg">${note}</p>
        ${social ? `<div class="aj-ev-rsvp-done__social"><span class="aj-ev-rsvp-done__follow">Follow along for more events</span><div class="aj-ev-rsvp-done__links">${social}</div></div>` : ''}
        <button type="button" class="aj-ev-btn aj-ev-btn--primary aj-ev-rsvp-done__close" data-aj="rsvp-close">Done</button>
      </div>`;
  }

  /**
   * Tell the site someone reached for the button — the form opening, or a trip
   * to a partner's ticket page. Sent as a beacon so an outbound click isn't
   * held up waiting for us, and so it still arrives once the page is gone.
   */
  function beacon(occurrenceId, kind) {
    const id = parseInt(occurrenceId, 10) || 0;
    if (!id || !cfg.restUrl) return;

    const url = `${cfg.restUrl}click`;
    const body = new URLSearchParams({ occurrence_id: String(id), kind }).toString();
    const type = 'application/x-www-form-urlencoded';

    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type }))) return;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': type },
        body,
        keepalive: true,
      });
    } catch (err) {
      // Never let counting a click get in the way of the click.
    }
  }

  document.addEventListener('click', (e) => {
    const openBtn = e.target.closest('[data-aj="rsvp-open"]');
    if (openBtn) {
      e.preventDefault();
      beacon(openBtn.dataset.occurrence, 'form_open');
      openPanel(parseInt(openBtn.dataset.occurrence, 10) || 0, openBtn.dataset.waitlist === '1');
    }
    const extBtn = e.target.closest('[data-aj="rsvp-ext"]');
    if (extBtn) beacon(extBtn.dataset.occurrence, 'external');
    if (e.target.closest('[data-aj="rsvp-close"]')) { e.preventDefault(); closePanel(); }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && panel && !panel.hidden) closePanel();
  });

  // Click a host/presenter face to view a larger version (lightweight, inline-
  // styled lightbox — works for the dynamically-swapped people block too).
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[data-aj-photo]');
    if (!link) return;
    e.preventDefault();

    const overlay = document.createElement('div');
    overlay.className = 'aj-ev-lightbox';
    overlay.setAttribute('style', 'position:fixed;inset:0;z-index:100000;background:rgba(10,16,24,.86);display:flex;align-items:center;justify-content:center;padding:24px;cursor:zoom-out;');

    const img = document.createElement('img');
    img.src = link.getAttribute('href');
    img.alt = link.getAttribute('aria-label') || '';
    img.setAttribute('style', 'max-width:92vw;max-height:92vh;border-radius:12px;box-shadow:0 12px 48px rgba(0,0,0,.5);');
    overlay.appendChild(img);

    const close = () => { overlay.remove(); document.removeEventListener('keydown', onKey); };
    function onKey(ev) { if (ev.key === 'Escape') close(); }
    overlay.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    document.body.appendChild(overlay);
  });

  // Ticket-count stepper (− / value / +) with a per-event maximum, plus
  // optional attendee name inputs for tickets 2..N.
  const DEFAULT_MAX = 10; // Absolute fallback when an event sets no limit.
  const qtyInput = $('[data-aj="rsvp-qty"]');
  const attendeesWrap = $('[data-aj="rsvp-attendees"]');
  const qtyDec = $('[data-aj="qty-dec"]');
  const qtyInc = $('[data-aj="qty-inc"]');
  const qtyHint = $('[data-aj="qty-hint"]');
  let qtyMax = DEFAULT_MAX;

  /**
   * Effective max tickets for the current occurrence.
   *
   * @returns {number} Clamp ceiling.
   */
  function currentMax() {
    const cap = current && Number(current.max_tickets) > 0
      ? Number(current.max_tickets) : DEFAULT_MAX;
    return Math.max(1, cap);
  }

  /**
   * Rebuild attendee name fields + button/hint state for a ticket count.
   *
   * @param {number} n Ticket count.
   * @returns {void}
   */
  function renderAttendees(n) {
    if (!attendeesWrap) return;
    // Some events (e.g. paid) require a name for every ticket.
    const required = attendeesWrap.getAttribute('data-require-names') === '1';
    const suffix = required ? '' : ' (optional)';
    const attr = required ? ' required' : '';
    let html = '';
    for (let i = 2; i <= n; i++) {
      html += `<div class="aj-ev-field"><label>Guest ${i} name${suffix}<input type="text" name="attendee_${i}"${attr} /></label></div>`;
    }
    attendeesWrap.innerHTML = html;
  }

  /**
   * Clamp + apply a ticket count to the stepper.
   *
   * @param {number} n Desired count.
   * @returns {void}
   */
  function setQty(n) {
    if (!qtyInput) return;
    const val = Math.min(qtyMax, Math.max(1, n || 1));
    qtyInput.value = String(val);
    if (qtyDec) qtyDec.disabled = val <= 1;
    if (qtyInc) qtyInc.disabled = val >= qtyMax;
    renderAttendees(val);
  }

  // ---- Ticket-type picker (multi-tier paid events) ------------------------
  const picker = $('[data-aj="ticketpicker"]');
  const totalEl = $('[data-aj="rsvp-total"]');
  const tierRows = picker ? [...picker.querySelectorAll('.aj-ev-ticktype')] : [];
  const currencyCode = picker ? (picker.dataset.currency || 'USD') : 'USD';
  const tierQty = (r) => parseInt(r.querySelector('[data-aj="tt-qty"]').value, 10) || 0;
  const tierPrice = (r) => parseInt(r.dataset.price, 10) || 0;
  const pickerTotal = () => tierRows.reduce((s, r) => s + tierQty(r), 0);
  const pickerSubtotal = () => tierRows.reduce((s, r) => s + tierQty(r) * tierPrice(r), 0);

  function refreshPicker() {
    const total = pickerTotal();
    tierRows.forEach((r) => {
      r.querySelector('[data-aj="tt-dec"]').disabled = tierQty(r) <= 0;
      r.querySelector('[data-aj="tt-inc"]').disabled = total >= qtyMax;
    });
    if (totalEl) {
      totalEl.textContent = total > 0
        ? `Subtotal: ${currencyCode} ${(pickerSubtotal() / 100).toFixed(2)}` : '';
    }
    renderAttendees(total);
  }

  function setTierQty(row, n) {
    const input = row.querySelector('[data-aj="tt-qty"]');
    const others = pickerTotal() - tierQty(row);
    input.value = String(Math.max(0, Math.min(n, qtyMax - others)));
    refreshPicker();
  }

  if (picker) {
    picker.addEventListener('click', (e) => {
      const inc = e.target.closest('[data-aj="tt-inc"]');
      const dec = e.target.closest('[data-aj="tt-dec"]');
      if (!inc && !dec) return;
      const row = e.target.closest('.aj-ev-ticktype');
      setTierQty(row, tierQty(row) + (inc ? 1 : -1));
    });
  }

  /**
   * Reset the stepper when (re)opening the panel for an occurrence.
   *
   * @returns {void}
   */
  function resetQty() {
    qtyMax = currentMax();

    // Multi-tier picker: zero every tier, refresh totals.
    if (picker) {
      tierRows.forEach((r) => { r.querySelector('[data-aj="tt-qty"]').value = '0'; });
      const code = $('[data-aj="rsvp-code"]');
      if (code) code.value = '';
      refreshPicker();
      return;
    }

    if (qtyHint) {
      // Per-registration limit only — no availability/seats are surfaced.
      if (current && Number(current.max_tickets) > 0) {
        qtyHint.textContent = `Up to ${qtyMax} per registration`;
      } else {
        qtyHint.textContent = '';
      }
    }
    setQty(1);
  }

  if (qtyDec) qtyDec.addEventListener('click', () => setQty((parseInt(qtyInput.value, 10) || 1) - 1));
  if (qtyInc) qtyInc.addEventListener('click', () => setQty((parseInt(qtyInput.value, 10) || 1) + 1));

  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const msg = $('[data-aj="rsvp-msg"]');
      const submitBtn = form.querySelector('[type="submit"]');
      const fd = new FormData(form);

      const payload = {
        occurrence_id: parseInt(fd.get('occurrence_id'), 10),
        first_name: fd.get('first_name'),
        last_name: fd.get('last_name'),
        email: fd.get('email'),
        phone: fd.get('phone'),
        note: fd.get('note') || '',
        aj_hp: fd.get('aj_hp') || '',
        newsletter: fd.get('newsletter') ? 1 : 0,
      };
      if (waitlistMode) payload.waitlist = 1;

      // Tickets: per-tier map for the picker, else a single quantity.
      let qty;
      if (picker) {
        const tickets = {};
        tierRows.forEach((r) => { const q = tierQty(r); if (q > 0) tickets[r.dataset.key] = q; });
        qty = pickerTotal();
        if (qty < 1) { msg.textContent = 'Please choose at least one ticket.'; return; }
        payload.tickets = tickets;
        const code = (fd.get('code') || '').trim();
        if (code) payload.code = code;
      } else {
        qty = Math.max(1, parseInt(fd.get('quantity'), 10) || 1);
        payload.quantity = qty;
      }

      const attendees = [];
      for (let i = 2; i <= qty; i++) {
        const name = fd.get(`attendee_${i}`);
        if (name) attendees.push({ name });
      }
      payload.attendees = attendees;

      msg.textContent = 'Submitting…';
      submitBtn.disabled = true;

      try {
        const headers = { 'Content-Type': 'application/json' };
        if (cfg.nonce) headers['X-WP-Nonce'] = cfg.nonce;
        const res = await fetch(`${cfg.restUrl}register`, {
          method: 'POST',
          headers,
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          msg.textContent = data.message || 'Something went wrong. Please try again.';
          submitBtn.disabled = false;
          return;
        }

        if (data.checkout_url) {
          window.location.href = data.checkout_url; // paid: off to Stripe.
          return;
        }

        showRegistered(!!data.waitlist);

        // Announce it so add-ons (the buddy system) can ask for anything extra
        // — but only now the place is actually recorded, so a drop-out at that
        // point costs the extra, never the registration.
        document.dispatchEvent(new CustomEvent('aj:registered', {
          detail: {
            occurrenceId: current ? current.id : 0,
            waitlist: !!data.waitlist,
          },
        }));
      } catch (err) {
        msg.textContent = 'Network error. Please try again.';
        submitBtn.disabled = false;
      }
    });
  }

  // ---- Post-event feedback form -------------------------------------------
  function bindFeedbackForm() {
    const fb = document.querySelector('[data-aj-feedback]');
    if (!fb) return;

    const stars = [...fb.querySelectorAll('.aj-ev-fb__star')];
    const ratingInput = fb.querySelector('[data-aj="fb-rating"]');
    const fbForm = fb.querySelector('[data-aj="fb-form"]');
    const fbMsg = fb.querySelector('[data-aj="fb-msg"]');

    const paintStars = (val) => {
      stars.forEach((s) => {
        const on = parseInt(s.dataset.val, 10) <= val;
        s.classList.toggle('is-on', on);
        s.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    };

    stars.forEach((s) => {
      s.addEventListener('click', () => {
        const val = parseInt(s.dataset.val, 10);
        ratingInput.value = String(val);
        paintStars(val);
      });
    });

    // Pre-select the rating when arriving from a star tapped in the email.
    const preset = parseInt(new URLSearchParams(window.location.search).get('aj_ev_rating'), 10);
    if (preset >= 1 && preset <= 5) {
      ratingInput.value = String(preset);
      paintStars(preset);
    }

    fbForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = fbForm.querySelector('button[type="submit"]');
      const payload = {
        token: fb.dataset.token || '',
        rating: parseInt(ratingInput.value, 10) || 0,
        comment: fbForm.querySelector('[data-aj="fb-comment"]').value || '',
        public: fbForm.querySelector('[data-aj="fb-public"]').checked ? '1' : '0',
      };

      if (!payload.rating && !payload.comment.trim()) {
        fbMsg.textContent = 'Please add a rating or a comment.';
        return;
      }

      fbMsg.textContent = 'Sending…';
      btn.disabled = true;

      try {
        const headers = { 'Content-Type': 'application/json' };
        if (cfg.nonce) headers['X-WP-Nonce'] = cfg.nonce;
        const res = await fetch(`${cfg.restUrl}feedback`, {
          method: 'POST',
          headers,
          credentials: 'same-origin',
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          fbMsg.textContent = data.message || 'Something went wrong. Please try again.';
          btn.disabled = false;
          return;
        }

        fbForm.innerHTML = `<p class="aj-ev-fb__thanks">${data.message || 'Thank you — your feedback means a lot!'}</p>`;
      } catch (err) {
        fbMsg.textContent = 'Network error. Please try again.';
        btn.disabled = false;
      }
    });
  }

  // ---- Event digest subscribe forms ---------------------------------------
  function bindSubscribeForms() {
    document.querySelectorAll('[data-aj-subscribe] [data-aj="sub-form"]').forEach((subForm) => {
      const msg = subForm.querySelector('[data-aj="sub-msg"]');
      subForm.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = subForm.querySelector('button[type="submit"]');
        const target = (subForm.querySelector('[data-aj="sub-target"]').value || 'all:');
        const [type, value] = target.split(':');
        const payload = {
          email: subForm.querySelector('input[name="email"]').value || '',
          type: type || 'all',
          value: value || '',
          aj_hp: (subForm.querySelector('input[name="aj_hp"]') || {}).value || '',
        };

        if (!payload.email) { msg.textContent = 'Please enter your email.'; return; }

        msg.textContent = 'Subscribing…';
        btn.disabled = true;

        try {
          const headers = { 'Content-Type': 'application/json' };
          if (cfg.nonce) headers['X-WP-Nonce'] = cfg.nonce;
          const res = await fetch(`${cfg.restUrl}subscribe`, {
            method: 'POST',
            headers,
            credentials: 'same-origin',
            body: JSON.stringify(payload),
          });
          const data = await res.json().catch(() => ({}));

          if (!res.ok) {
            msg.textContent = data.message || 'Something went wrong. Please try again.';
            btn.disabled = false;
            return;
          }

          subForm.innerHTML = `<p class="aj-ev-subscribe__thanks">${data.message || 'Almost there — check your email to confirm.'}</p>`;
        } catch (err) {
          msg.textContent = 'Network error. Please try again.';
          btn.disabled = false;
        }
      });
    });
  }
})();
